import { DidomiUtils } from '../utils/DidomiUtils';
import { isAllowed } from './utils';
import { createCookieRepository } from '../repository/cookies';
import { createStorageRepository } from '../repository/storage';

const noop = () => {};

/**
 * Initializes the CMP client using a single configuration object.
 * @param {Object} params
 * @param {string} params.brand - Brand code (e.g. 'ED', 'OP', 'GO', 'TR').
 * @param {string} params.locale - Locale for the CMP banner (e.g. 'en-US').
 * @param {boolean} [params.debug=false] - Enables CMP debug mode.
 * @param {string} [params.moduleName=''] - Name of the calling module, forwarded in consent change events.
 * @param {string} [params.moduleVersion=''] - Version of the calling module, forwarded in consent change events.
 * @returns {Promise<void>}
 */
const initWithParams = async ({
  brand,
  locale,
  debug = false,
  moduleName = '',
  moduleVersion = '',
}) => {
  // guards against overlapping calls while window.Didomi is still loading
  if (debug) {
    global.isCMPDebugMode = true;
  }
  if (DidomiUtils.isAlreadyInitialized() || DidomiUtils.isInitializing()) {
    await DidomiUtils.loadDidomi();
    return;
  }
  DidomiUtils.setDidomiConfig(brand, locale);
  DidomiUtils.setDidomiEvents(locale, moduleName, moduleVersion);
  await DidomiUtils.loadDidomi();
};

/**
 * @deprecated Positional arguments are deprecated, pass a single { brand, locale, debug, moduleName, moduleVersion } object instead (see initWithParams).
 * @param {string|Object} brandOrConfig - Brand code when using the deprecated positional form, or a config object (see initWithParams) to use the new single-object form.
 * @param {string} [locale] - Locale, only used with the deprecated positional form.
 * @param {boolean} [debug=false] - Debug mode, only used with the deprecated positional form.
 * @returns {Promise<void>}
 */
const init = async (brandOrConfig, locale, debug = false) => {
  if (typeof brandOrConfig === 'string') {
    if (!locale) {
      throw new TypeError(
        'init(brand, locale, debug) requires a locale when using positional arguments'
      );
    }
    return initWithParams({ brand: brandOrConfig, locale, debug });
  }

  if (!brandOrConfig || typeof brandOrConfig !== 'object') {
    throw new TypeError('init(config) requires a configuration object');
  }

  const {
    brand,
    locale: configLocale,
    moduleName,
    moduleVersion,
    debug: configDebug = false,
  } = brandOrConfig;

  return initWithParams({
    brand,
    locale: configLocale,
    moduleName,
    moduleVersion,
    debug: configDebug,
  });
};

const cmpClient = {
  cookie: createCookieRepository(),
  localStorage: createStorageRepository('localStorage'),
  sessionStorage: createStorageRepository('sessionStorage'),
};
const get = type => cmpClient[type];

const createSuccessCMPClient = () => {
  const {
    listenChangeUserConsent,
    stopListeningChangeUserConsent,
  } = DidomiUtils.consentObserver;
  const successCMPClient = {
    status: 'success',
    init: () => Promise.resolve(successCMPClient),
    get,
    listenChangeUserConsent,
    stopListeningChangeUserConsent,
  };
  return successCMPClient;
};

const failedCMPClient = {
  get,
  status: 'error',
  init: () => Promise.resolve(failedCMPClient),
  listenChangeUserConsent: noop,
  stopListeningChangeUserConsent: noop,
};

export const createStorageClient = (website, locale, debug = false) => {
  if (!DidomiUtils.isAlreadyInitialized()) {
    return {
      get,
      status: 'idle',
      init: () =>
        init(website, locale, debug)
          .then(createSuccessCMPClient)
          .catch(() => failedCMPClient),
      listenChangeUserConsent: noop,
      stopListeningChangeUserConsent: noop,
    };
  }
  return createSuccessCMPClient();
};

/**
 * Registers a callback invoked whenever the user's CMP consent changes.
 * @param {Function} cb - Callback receiving `{ isAllowed }` when the consent changes.
 * @returns {void}
 */
const onChangeUserCMP = cb => {
  DidomiUtils.setCallback(cb);
};

export { isAllowed, onChangeUserCMP, init };
